import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from './redux/hooks';
import { useCountryCode } from './common/countryCode/useCountryCode';
import { useAuthentication } from './common/hooks/useAuthentication';
import { useUserRole } from './common/hooks/useUserRole';
import { SettingUtils } from './pages/Settings/SettingUtils';
import {
    setCompanyDetails,
    setInvoicingDetails,
    setIsQBUserAvailable,
    setSettingsList,
    setCommunicationDetails,
    setIsBolRequiredInMobileApp,
    setShowHeadCountOrQuantity,
    setPasswordExpirationMonths,
} from './pages/Settings/reducers/settingsReducer';
import { settingsApi } from './api/settings/settingsApi';
import { fetchTagGroups } from './components/TagGroup/TagGroupReducer';
import { PUBLIC_ROUTES } from './utils/routes';
import { useLocation } from 'react-router-dom';

interface IUseApp {
    showFreeTrialWarningModal: boolean;
    setShowFreeTrialWarningModal: (show: boolean) => void;
    daysLeft: string | null;
    showPaymentLink: string | null;
    handleShowFreeTrialWarningModal: (confirmed: boolean) => void;
    isAuthenticated: boolean;
    isProtectedRoute: (roles: string[]) => boolean;
    isPrivateRoute: boolean;
}

export const useApp = (): IUseApp => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { loadCountryCodeOptions } = useCountryCode();
    const { isAuthenticated } = useAuthentication();
    const { isProtectedRoute } = useUserRole();

    const daysLeft =
        localStorage.getItem('daysLeft') ?? sessionStorage.getItem('daysLeft');

    const chargeBeePaymentLink =
        localStorage.getItem('paymentLink') ??
        sessionStorage.getItem('paymentLink') ??
        '';

    const showPaymentLink =
        localStorage.getItem('showPaymentLink') ??
        sessionStorage.getItem('showPaymentLink');

    const [showFreeTrialWarningModal, setShowFreeTrialWarningModal] = useState(
        daysLeft === '0',
    );

    const handleShowFreeTrialWarningModal = useCallback(
        (confirmed: boolean) => {
            if (!confirmed && showPaymentLink) {
                window.open(chargeBeePaymentLink, '_blank');
            }
            if (daysLeft !== '0') {
                setShowFreeTrialWarningModal(false);
            }
        },
        [chargeBeePaymentLink, showPaymentLink, daysLeft],
    );

    const fetchSettings = useCallback(async () => {
        const settings = await settingsApi.fetchSettings();
        const settingsDetail = new SettingUtils(settings);
        const isAvailable = await settingsDetail.getQBUsers();
        dispatch(setIsQBUserAvailable(isAvailable));
        const companyDetails = settingsDetail.getCompanyDetails();
        const settingsData = await settingsDetail.fetchTenantSettings();
        dispatch(setCompanyDetails(settingsData ?? companyDetails));
        dispatch(setInvoicingDetails(settingsDetail.getInvoicingDetails()));
        dispatch(
            setCommunicationDetails(settingsDetail.getCommunicationDetails()),
        );
        dispatch(
            setIsBolRequiredInMobileApp(
                settingsDetail.getIsBolRequiredInMobileApp(),
            ),
        );
        dispatch(
            setShowHeadCountOrQuantity(
                settingsDetail.getShowHeadCountOrQuantity(),
            ),
        );
        dispatch(
            setPasswordExpirationMonths(
                settingsDetail.getPasswordExpiryMonths(),
            ),
        );
        dispatch(setSettingsList(settings));
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchTagGroups());
            fetchSettings();
        }
    }, [isAuthenticated, dispatch, fetchSettings]);

    useEffect(() => {
        loadCountryCodeOptions();
    }, [loadCountryCodeOptions]);

    useEffect(() => {
        if (daysLeft === '0') {
            setShowFreeTrialWarningModal(true);
        }
    }, [daysLeft]);

    const isPrivateRoute =
        isAuthenticated &&
        !PUBLIC_ROUTES.find(route => route.path === location.pathname);

    return {
        showFreeTrialWarningModal,
        setShowFreeTrialWarningModal,
        daysLeft,
        showPaymentLink,
        handleShowFreeTrialWarningModal,
        isAuthenticated,
        isProtectedRoute,
        isPrivateRoute,
    };
};
